import * as React from 'react';
import { useMutation } from 'redux-query-react';
import { useContext, useState } from 'react';
import Menu from 'automaailm-ui/lib/Components/Menu';
import MenuItem from 'automaailm-ui/lib/Components/MenuItem';
import { IAddress, ICustomer } from '../../../interfaces/checkout/customer/ICustomer';
import LayoutSplit from 'automaailm-ui/lib/Layouts/LayoutSplit';
import LayoutSplitColumn from 'automaailm-ui/lib/Layouts/LayoutSplitColumn';
import ExistingAddresses from '../../../components/address/existingAddresses';
import LayoutForm from 'automaailm-ui/lib/Layouts/LayoutForm';
import FormRow from 'automaailm-ui/lib/Components/FormRow';
import { useTranslation } from 'react-i18next';
import RadioWithLabel from 'automaailm-ui/lib/Components/RadioWithLabel';
import { postRequest } from 'data/requests/postRequest';
import { useEffect } from 'react';
import { IPaymentMethodResponse } from '../../../interfaces/payment/IPaymentMethodResponse';
import HandleMethod, { PaymentMethodEnum } from '../../../components/checkout/Payment/Methods/MethodHandler';
import { ResponseStatusEnum } from '../../../enums/ResponseStatus';
import RedirectAbsolute, { IRedirectProps } from '../../../components/Redirect/RedirectAbsolute';
import SelectedAddressToPostAddress from '../../../components/address/selectedAddressToPostAddress';
import Toaster from 'automaailm-ui/lib/Components/Toaster';
import { isLoggedIn } from '../../../helpers/customer/isLoggedIn';
import ExistingAddressForm from '../../../components/checkout/Address/ExistingAddressForm';
import NewAddressOverlay from '../../../components/address/NewAddressOverlay';
import BusinessAddressForm from '../../../components/checkout/Address/BusinessAddressForm';
import { empty } from '../../../helpers/empty';
import { IPaymentMethodExtraData } from '../../../interfaces/payment/IPaymentMethodExtraData';
import { useRef } from 'react';
import Banklinks from '../../../components/checkout/Payment/Banklinks';
import { MutableRefObject } from 'react';
import { ICart } from '../../../interfaces/checkout/cart/cartType';
import Agreements from '../../../components/checkout/Payment/Agreements';
import LoadingOverlay from '../../../components/checkout/Overlay/LoadingOverlay';
import useOverlays from '../../../components/overlay/Overlay';
import Subscription from '../../../components/checkout/Payment/Subscription';
import { IShippingMethod } from '../../../interfaces/checkout/shipping/IShippingMethod';
import PersonalIdForm from '../../../components/checkout/Payment/PersonalIdForm';
import { CartContext } from '../../../components/checkout/Content';

export interface IPaymentOnClickInput {
    paymentMethodExtraData?: IPaymentMethodExtraData;
    redirect?: IRedirectProps;
    rawMethodData?: any;
}

export interface IPaymentMethodProps {
    method: IPaymentMethodResponse;
    clientType: CLIENT_TYPE;
    paymentMethodExtraData: MutableRefObject<IPaymentMethodExtraData | undefined> | undefined;
    onClick: (input: IPaymentOnClickInput) => void;
    cart: ICart | undefined;
    email: string | undefined;
    setAllowQuery: (allowQuery: boolean) => void;
    extensionAttributes: () => {
        risks_awareness?: string;
        agreement_ids?: string[];
    };
    shippingMethod: IShippingMethod | null;
}

interface IProps {
    customer: ICustomer | null;
    paymentMethods: IPaymentMethodResponse[] | null;
    email: string | undefined;
    hasMedicine: boolean;
    cart: ICart | undefined;
    selectedMethod: IShippingMethod | null;
}

export interface ITerms {
    id: string;
    accepted: boolean;
}

export enum CLIENT_TYPE {
    CLIENT_BUSINESS = 'business',
    CLIENT_REGULAR = 'client',
}

const COUNSELING_STATUS_HAS_NOT_ANSWERED = 0;
const COUNSELING_STATUS_HAS_BEEN_COUNSELED = 1;
const COUNSELING_STATUS_DOES_NOT_WANT = 2;

const PaymentBlock = (props: IProps) => {
    const { t } = useTranslation();
    const { openOverlay, closeOverlay } = useOverlays();
    const { customer, paymentMethods, email, hasMedicine, cart } = props;
    const allowQuery = useRef(false);
    const [updateState, setUpdateState] = useState(false);
    const [personalIdSet, setPersonalIdSet] = useState(false);
    const cartContext = useContext(CartContext);
    const [redirectUrl, setRedirectUrl] = useState<IRedirectProps | undefined>();
    const [methodCode, setMethodCode] = useState<string>();
    const [selectedAddress, selectAddress] = useState<IAddress>();
    const [errors, setErrors] = useState(false);
    const [clientType, setClientType] = useState(CLIENT_TYPE.CLIENT_REGULAR);
    const [terms, setTerms] = useState<ITerms[]>(
        window.agreements?.totalRecords > 0
            ? window.agreements.items.map((item) => ({
                  id: item.agreement_id,
                  accepted: false,
              }))
            : [],
    );
    const [newAddress, selectNewAddress] = useState<IAddress>();
    const paymentMethodRenderers: React.Component<any, any>[] = [];
    const rawData = useRef();
    const paymentMethodExtraData = useRef<Partial<IPaymentMethodExtraData>>();

    const [councellingStatus, setCouncellingStatus] = useState(COUNSELING_STATUS_HAS_NOT_ANSWERED);
    const addresses = ExistingAddresses(true, selectedAddress, selectAddress, null, customer, null).filter(
        (address) => address.isBusiness,
    );

    const [{ isFinished, status }, postData] = useMutation((data, url) =>
        postRequest({ type: 'response', url, data, useStoreCode: true }),
    );
    const getExtensionAttributes = () => {
        const risksAwareness =
            councellingStatus === COUNSELING_STATUS_DOES_NOT_WANT
                ? 'im_aware_of_risks_myself'
                : councellingStatus === COUNSELING_STATUS_HAS_BEEN_COUNSELED
                ? 'advised_by_pharmacist'
                : undefined;
        return {
            risks_awareness: risksAwareness,
            agreement_ids: terms.filter((item) => item.accepted).map((item) => item.id),
        };
    };
    const setAllowQuery = (state: boolean) => {
        allowQuery.current = state;
        setUpdateState(!updateState);
    };

    if (!paymentMethodRenderers.length) {
        const isFreePaymentMethod = paymentMethods?.find((method) => method.code === PaymentMethodEnum.free);

        let MethodComponent;

        // free payment method is returned only if grandTotal is <=0, if presents show only free payment method
        if (isFreePaymentMethod) {
            const onClick = (input: IPaymentOnClickInput) => {
                rawData.current = input.rawMethodData;
                setMethodCode(isFreePaymentMethod.code);
                paymentMethodExtraData.current = input.paymentMethodExtraData;
                setRedirectUrl(input.redirect);
                window.dispatchEvent(new CustomEvent('business-address-save'));
                window.dispatchEvent(new CustomEvent('subscription-save'));
                if (clientType !== CLIENT_TYPE.CLIENT_BUSINESS || (selectedAddress && !selectedAddress.isNew)) {
                    setAllowQuery(true);
                }
            };
            MethodComponent = HandleMethod({
                method: isFreePaymentMethod,
                clientType,
                paymentMethodExtraData,
                onClick,
                cart,
                email,
                extensionAttributes: getExtensionAttributes,
                setAllowQuery,
                shippingMethod: props.selectedMethod,
            });
            paymentMethodRenderers.push(MethodComponent);
        }

        paymentMethods?.forEach((method: IPaymentMethodResponse, index, array) => {
            if (isFreePaymentMethod) {
                return;
            }
            const onClick = (input: IPaymentOnClickInput) => {
                rawData.current = input.rawMethodData;
                setMethodCode(method.code);
                paymentMethodExtraData.current = input.paymentMethodExtraData;
                setRedirectUrl(input.redirect);
                window.dispatchEvent(new CustomEvent('business-address-save'));
                window.dispatchEvent(new CustomEvent('subscription-save'));
                window.dispatchEvent(
                    new CustomEvent('checkout-step-proceed', {
                        detail: {
                            action: 'add-payment-info',
                            method: { methodCode: method.code, methodName: input?.rawMethodData?.name },
                            items: cartContext.cartItems,
                            customer: cartContext.customer,
                        },
                    }),
                );
                if (clientType !== CLIENT_TYPE.CLIENT_BUSINESS || (selectedAddress && !selectedAddress.isNew)) {
                    setAllowQuery(true);
                }
            };

            MethodComponent = HandleMethod({
                method,
                clientType,
                paymentMethodExtraData,
                onClick,
                cart,
                email,
                extensionAttributes: getExtensionAttributes,
                setAllowQuery,
                shippingMethod: props.selectedMethod,
            });

            if (MethodComponent && !paymentMethodRenderers.includes(MethodComponent)) {
                paymentMethodRenderers.push(MethodComponent);
            }
        });
    }

    const queryAction = () => {
        if (allowQuery.current) {
            let hasErrors =
                !!terms.find((term) => !term.accepted) ||
                (hasMedicine && councellingStatus === COUNSELING_STATUS_HAS_NOT_ANSWERED);
            if (!!window.isPersonalCodeEnabled && clientType !== CLIENT_TYPE.CLIENT_BUSINESS && !personalIdSet) {
                hasErrors = true;
            }
            setErrors(hasErrors);
            if (!hasErrors && methodCode) {
                openOverlay('payment-loading-overlay', true);
                setAllowQuery(false);
                let address;
                if (selectedAddress && clientType === CLIENT_TYPE.CLIENT_BUSINESS) {
                    address = SelectedAddressToPostAddress(selectedAddress);
                }

                const addressInformation = {
                    ...paymentMethodExtraData.current?.extraParams,
                    email,
                    billing_address: typeof address !== 'undefined' ? address : undefined,
                    paymentMethod: {
                        method: methodCode,
                        extension_attributes: getExtensionAttributes(),
                        additional_data: { ...paymentMethodExtraData.current?.additionalData },
                    },
                };
                const url =
                    paymentMethodExtraData.current?.paymentVerificationUrl ||
                    (isLoggedIn
                        ? 'carts/mine/payment-information'
                        : `guest-carts/${window.quoteIdMask}/payment-information`);
                postData(
                    {
                        ...addressInformation,
                    },
                    url,
                ).then((response) => {
                    const responseSuccessful = response && response.status === ResponseStatusEnum.ok;
                    if (responseSuccessful) {
                        window.dispatchEvent(
                            new CustomEvent('payment-response-receive', {
                                detail: {
                                    methodCode,
                                    paymentResponse: response.body,
                                    rawData: rawData.current,
                                    setRedirectUrl,
                                    customer,
                                },
                            }),
                        );
                    }

                    if (!responseSuccessful && response?.body?.message) {
                        closeOverlay('payment-loading-overlay', false);
                        setMethodCode(undefined);
                        setAllowQuery(true);

                        Toaster.addToast({
                            intent: 'danger',
                            text: response.body.message,
                            asHtml: true,
                        });
                    }
                    if (response.body && response.body.code === 455) {
                        setRedirectUrl({ to: `checkout/cart` });
                    }
                });
            } else {
                setMethodCode(undefined);
            }
        }
    };
    useEffect(queryAction);
    useEffect(() => {
        if (isFinished && redirectUrl && !empty(redirectUrl.to)) {
            if (status === 400) {
                setTimeout(() => RedirectAbsolute({ to: `checkout` }), 2000);
            } else {
                RedirectAbsolute(redirectUrl);
            }
        }
        if (selectedAddress && selectedAddress.isNew) {
            selectNewAddress(selectedAddress);
        }
    });
    const addressSaveAction = () => {
        setMethodCode(undefined);
        setRedirectUrl(undefined);
        window.dispatchEvent(new CustomEvent('business-address-save'));
    };

    const newAddressForm = (
        <BusinessAddressForm
            proceedAction={() => setAllowQuery(true)}
            selectedAddress={newAddress}
            customer={customer}
            selectAddress={selectAddress}
        />
    );

    return (
        <LayoutSplit>
            {window.isBusinessEnabled && true ? (
                <LayoutSplitColumn>
                    <Menu layout="inline">
                        <MenuItem
                            text={t('checkout.Private customer')}
                            active={clientType === CLIENT_TYPE.CLIENT_REGULAR}
                            onClick={() => setClientType(CLIENT_TYPE.CLIENT_REGULAR)}
                        />
                        <MenuItem
                            text={t('checkout.Business customer')}
                            active={clientType === CLIENT_TYPE.CLIENT_BUSINESS}
                            onClick={() => setClientType(CLIENT_TYPE.CLIENT_BUSINESS)}
                        />
                    </Menu>
                    {clientType === CLIENT_TYPE.CLIENT_BUSINESS && (
                        <React.Fragment>
                            {!isLoggedIn && (
                                <p>
                                    {t('checkout.To use invoice payment method')}{' '}
                                    <a href={window.LOGIN_URL}>{t('checkout.please sign in')}</a>
                                </p>
                            )}
                            {customer &&
                            addresses &&
                            ((addresses.length === 1 && !addresses.find((address) => address.isNew)) ||
                                addresses.length > 1) ? (
                                <React.Fragment>
                                    <ExistingAddressForm
                                        proceedAction={() => setAllowQuery(true)}
                                        selectedAddress={selectedAddress}
                                        addresses={addresses}
                                        showButton={false}
                                        title={t(
                                            'checkout.Please select the company address and hit the Update button',
                                        )}
                                    />
                                    <NewAddressOverlay
                                        title={t('checkout.Add a business address')}
                                        newAddressForm={newAddressForm}
                                        proceedAction={() => addressSaveAction()}
                                    />
                                </React.Fragment>
                            ) : (
                                <React.Fragment>{newAddressForm}</React.Fragment>
                            )}
                        </React.Fragment>
                    )}
                </LayoutSplitColumn>
            ) : (
                <LayoutSplitColumn />
            )}
            <LayoutSplitColumn>
                {!!window.isPersonalCodeEnabled && clientType !== CLIENT_TYPE.CLIENT_BUSINESS && (
                    <PersonalIdForm setPersonalIdSet={setPersonalIdSet} errors={errors} />
                )}
                <Agreements terms={terms} setTerms={setTerms} errors={errors} />
                {!!window.isSubscriptionEnabled && <Subscription customer={customer} email={email} />}
                <LayoutForm layout="vertical">
                    {hasMedicine && (
                        <FormRow
                            required={true}
                            error={
                                errors && councellingStatus === COUNSELING_STATUS_HAS_NOT_ANSWERED
                                    ? t('This is a required field')
                                    : ''
                            }
                            label={<b>{t('checkout.labelCouncelling')}</b>}
                        >
                            <RadioWithLabel
                                label={t('checkout.labelCouncellingOption1')}
                                checked={councellingStatus === COUNSELING_STATUS_HAS_BEEN_COUNSELED}
                                onChange={() => setCouncellingStatus(COUNSELING_STATUS_HAS_BEEN_COUNSELED)}
                            />
                            <RadioWithLabel
                                label={t('checkout.labelCouncellingOption2')}
                                checked={councellingStatus === COUNSELING_STATUS_DOES_NOT_WANT}
                                onChange={() => setCouncellingStatus(COUNSELING_STATUS_DOES_NOT_WANT)}
                            />
                        </FormRow>
                    )}
                    <FormRow
                        label={<b>{t('checkout.Payment Method:')}</b>}
                        description={t('checkout.You will be redirected back to us after payment')}
                    >
                        <Banklinks components={paymentMethodRenderers} />
                    </FormRow>
                </LayoutForm>
            </LayoutSplitColumn>
            <LoadingOverlay index={'payment-loading-overlay'} />
        </LayoutSplit>
    );
};

export default PaymentBlock;
